import { useEffect, useState, type ChangeEvent } from 'react';
import { Layout } from '../components/Layout';
import { AISettings } from '../components/AISettings';
import { Card, Button, Input, Field, Modal } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface WlDomain {
  domain: string;
  zone_id: string;
  cert_arn: string;
  name_servers: string[];
  cert_status: string;
  delegated: boolean;
  attached: boolean;
  cdn_url: string;
  attach_error?: string;
  label?: string;
  script_name?: string;
  function_name?: string;
  campaign_param?: string;
  user_param?: string;
  cdn_host?: string;
}
interface WlBrand {
  label: string; domain: string; script_name: string; function_name: string;
  campaign_param: string; user_param: string; script_url: string; builtin: boolean;
  cdn_host?: string;
}
interface InfraCheck { id: string; label: string; ok: boolean; warn?: boolean; detail: string }
interface InfraResult { domain: string; host: string; ok: boolean; checked_at: string; checks: InfraCheck[]; actions?: string[] }

// Espelha suggestBrandConfig do backend — defaults derivados do domínio
function suggestBrand(domain: string) {
  const base = (domain.split('.')[0] || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  const cap = base.charAt(0).toUpperCase() + base.slice(1);
  return { label: base, script_name: base, function_name: `set${cap}Data`, campaign_param: `${/^[a-z]/.test(base) ? base : 'campaign' + cap}Id`, user_param: 'visitorId', cdn_prefix: 'cdn' };
}

// Host da tag a partir do subdomínio configurável ("@" = apex)
function tagHost(domain: string, prefix: string) {
  const p = prefix.trim() || 'cdn';
  return p === '@' ? domain : `${p}.${domain}`;
}

export default function Settings() {

  // White-label domains
  const [wlDomains, setWlDomains] = useState<WlDomain[]>([]);
  const [wlBrands, setWlBrands] = useState<WlBrand[]>([]);
  const [wlInput, setWlInput] = useState('');
  const [wlBrandForm, setWlBrandForm] = useState({ label: '', script_name: '', function_name: '', campaign_param: '', user_param: '', cdn_prefix: '' });
  const [wlBrandTouched, setWlBrandTouched] = useState(false);
  const [wlPrevLabel, setWlPrevLabel] = useState<string | null>(null); // marca em edição (rename usa como PK antiga)
  const [wlBusy, setWlBusy] = useState(false);
  const [wlLoading, setWlLoading] = useState(false);
  const [wlMsg, setWlMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  function loadWlDomains() {
    setWlLoading(true);
    api.get<ApiResponse<WlDomain[]>>('/settings/whitelabel-domains')
      .then((r) => setWlDomains(r.data || []))
      .catch(() => {})
      .finally(() => setWlLoading(false));
    api.get<ApiResponse<WlBrand[]>>('/whitelabel/brands')
      .then((r) => setWlBrands(r.data || []))
      .catch(() => {});
  }
  useEffect(() => { loadWlDomains(); }, []);

  // Ao digitar o domínio, sugere as variáveis da marca (editáveis)
  function onWlDomainChange(v: string) {
    setWlInput(v);
    if (!wlBrandTouched) setWlBrandForm(suggestBrand(v.trim()));
  }
  const setBrandField = (k: keyof typeof wlBrandForm) => (e: ChangeEvent<HTMLInputElement>) => {
    setWlBrandTouched(true);
    setWlBrandForm((f) => ({ ...f, [k]: e.target.value }));
  };

  function resetBrandForm() {
    setWlInput('');
    setWlBrandForm({ label: '', script_name: '', function_name: '', campaign_param: '', user_param: '', cdn_prefix: '' });
    setWlBrandTouched(false);
    setWlPrevLabel(null);
  }

  async function provisionDomain() {
    const domain = wlInput.trim();
    if (!domain) return;
    setWlBusy(true); setWlMsg(null);
    try {
      const r = await api.post<ApiResponse<{ domain: string; name_servers: string[] }>>('/settings/whitelabel-domains',
        { domain, ...wlBrandForm, prev_label: wlPrevLabel || undefined });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed');
      const wasEdit = !!wlPrevLabel;
      resetBrandForm();
      setWlMsg({ type: 'ok', text: wasEdit
        ? `${r.data.domain} brand updated — the audience picks it up within 60s.`
        : `${r.data.domain} provisioned — set the NS records below at the domain registrar.` });
      loadWlDomains();
    } catch (e) {
      setWlMsg({ type: 'err', text: e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed' });
    } finally {
      setWlBusy(false);
    }
  }

  // Preenche o form com uma marca dinâmica existente (Save = update; label novo = rename)
  function editBrand(b: WlBrand) {
    const host = b.cdn_host || `cdn.${b.domain}`;
    setWlInput(b.domain);
    setWlBrandTouched(true);
    setWlPrevLabel(b.label);
    setWlBrandForm({
      label: b.label,
      script_name: b.script_name,
      function_name: b.function_name,
      campaign_param: b.campaign_param,
      user_param: b.user_param,
      cdn_prefix: host === b.domain ? '@' : host.replace(`.${b.domain}`, ''),
    });
    setWlMsg(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function deleteWlDomain(domain: string) {
    if (!window.confirm(`Delete ${domain}?\n\nThis removes the brand config, the SSL certificate and the Route53 hosted zone. Tags using this domain will stop working.`)) return;
    setWlBusy(true); setWlMsg(null);
    try {
      const r = await api.post<ApiResponse<{ domain: string; actions: string[] }>>('/settings/whitelabel-domains/delete', { domain });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed');
      setWlMsg({ type: 'ok', text: `${r.data.domain} deleted — ${r.data.actions.join(' · ') || 'nothing to remove'}` });
      loadWlDomains();
    } catch (e) {
      setWlMsg({ type: 'err', text: e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to delete' });
    } finally {
      setWlBusy(false);
    }
  }

  // Modal de health check da infra do domínio (zona, NS, DNS, ALB, cert, HTTPS)
  const [infraOpen, setInfraOpen] = useState(false);
  const [infraBusy, setInfraBusy] = useState(false);
  const [infraDomain, setInfraDomain] = useState('');
  const [infraData, setInfraData] = useState<InfraResult | null>(null);
  function openInfraCheck(domain: string) {
    setInfraDomain(domain);
    setInfraData(null);
    setInfraOpen(true);
    setInfraBusy(true);
    api.get<ApiResponse<InfraResult>>(`/settings/whitelabel-domains/check?domain=${encodeURIComponent(domain)}`)
      .then((r) => setInfraData(r.data || null))
      .catch((e) => setWlMsg({ type: 'err', text: e instanceof ApiError ? e.message : 'Failed to check domain' }))
      .finally(() => setInfraBusy(false));
  }
  async function fixInfra() {
    setInfraBusy(true);
    try {
      const r = await api.post<ApiResponse<InfraResult>>('/settings/whitelabel-domains/fix', { domain: infraDomain });
      if (!r.success || !r.data) throw new Error(r.error || r.message || 'Failed');
      setInfraData(r.data);
    } catch (e) {
      setWlMsg({ type: 'err', text: e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to fix' });
    } finally {
      setInfraBusy(false);
    }
  }

  function copyNs(d: WlDomain) {
    navigator.clipboard.writeText(d.name_servers.join('\n')).then(() => {
      setCopied(d.domain);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <Layout title="Platform Settings">
      <AISettings />

      {/* White-label domains: provisiona zona + cert + cdn e devolve os NS */}
      <Card className="p-6 max-w-3xl mt-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold text-ink-900">🌐 White-label Domains</h2>
          <Button variant="outline" onClick={loadWlDomains} disabled={wlLoading}>{wlLoading ? '…' : '↻ Refresh status'}</Button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Enter the new brand's domain. The system creates the Route53 hosted zone, requests the SSL certificate (auto-validated),
          points the tag host (<code className="bg-surface-muted px-1 rounded">cdn.&lt;domain&gt;</code> by default — the CDN subdomain is configurable below) to the audience server and returns the NS records to configure at the registrar.
          Once the delegation propagates, the certificate is attached to the load balancer automatically.
        </p>
        {wlMsg && <div className={`mb-4 rounded-lg text-sm px-4 py-3 border ${wlMsg.type === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>{wlMsg.text}</div>}
        <div className="flex gap-2 mb-3">
          <Input value={wlInput} onChange={(e) => onWlDomainChange(e.target.value)} placeholder="e.g. newbrand.com"
            onKeyDown={(e) => { if (e.key === 'Enter') provisionDomain(); }} />
          <Button onClick={provisionDomain} disabled={wlBusy || !wlInput.trim()} className="whitespace-nowrap">
            {wlBusy ? 'Saving…' : wlPrevLabel ? '✓ Save changes' : '+ Provision'}
          </Button>
          {wlPrevLabel && <Button variant="outline" onClick={resetBrandForm} className="whitespace-nowrap">Cancel</Button>}
        </div>
        <div className="rounded-lg border border-border p-4 mb-5">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">
            {wlPrevLabel ? <>Editing brand <span className="normal-case text-ink-900">{wlPrevLabel}</span> <span className="font-normal normal-case">(change anything, including the label, and save)</span></>
              : <>Brand variables <span className="font-normal normal-case">(auto-suggested when you type the domain — adjust anything before provisioning)</span></>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
            <Field label="Label" help="client label"><Input value={wlBrandForm.label} onChange={setBrandField('label')} placeholder="perfdata" /></Field>
            <Field label="CDN subdomain" help={`"@" = no subdomain`}><Input value={wlBrandForm.cdn_prefix} onChange={setBrandField('cdn_prefix')} placeholder="cdn" /></Field>
            <Field label="Script" help={`${wlBrandForm.script_name || 'name'}.min.js`}><Input value={wlBrandForm.script_name} onChange={setBrandField('script_name')} placeholder="perf" /></Field>
            <Field label="Function" help="global JS function"><Input value={wlBrandForm.function_name} onChange={setBrandField('function_name')} placeholder="setPerfData" /></Field>
            <Field label="Campaign param" help="Used automatically by the tag, pixel and reports"><Input value={wlBrandForm.campaign_param} onChange={setBrandField('campaign_param')} placeholder="perfId" /></Field>
            <Field label="User param" help="Must differ from Campaign param"><Input value={wlBrandForm.user_param} onChange={setBrandField('user_param')} placeholder="visitorId" /></Field>
          </div>
          <div className="mt-3 rounded bg-slate-900 text-slate-100 p-3 text-[11px] font-mono leading-relaxed overflow-x-auto">
            <div className="text-slate-400 font-sans font-semibold uppercase text-[10px] tracking-wide mb-1">Tag preview</div>
            <div className="whitespace-nowrap">{`<script src="https://${tagHost(wlInput.trim() || '<domain>', wlBrandForm.cdn_prefix)}/${wlBrandForm.script_name || '<script>'}.min.js"></script>`}</div>
            <div className="whitespace-nowrap">{`${wlBrandForm.function_name || '<function>'}({${wlBrandForm.campaign_param || '<param>'}: '...', eventType: 'home'});`}</div>
          </div>
        </div>

        {wlDomains.length === 0 && !wlLoading && <p className="text-sm text-slate-400">No domains provisioned yet.</p>}
        <div className="space-y-4">
          {wlDomains.map((d) => (
            <div key={d.domain} className="rounded-lg border border-border overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-surface-muted/40">
                <button className="font-semibold text-ink-900 hover:text-brand-600 hover:underline" title="Check infrastructure health"
                  onClick={() => openInfraCheck(d.domain)}>🩺 {d.domain}</button>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${d.delegated ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {d.delegated ? '✓ DNS' : '⏳ waiting for NS'}
                </span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${d.cert_status === 'ISSUED' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {d.cert_status === 'ISSUED' ? '✓ certificate' : `⏳ cert: ${d.cert_status.toLowerCase().replace(/_/g, ' ')}`}
                </span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${d.attached ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {d.attached ? '✓ load balancer' : '— load balancer'}
                </span>
                <div className="ml-auto flex items-center gap-3">
                  <a className="text-xs text-brand-600 hover:underline" href={d.cdn_url} target="_blank" rel="noreferrer">{d.cdn_url.replace('https://', '')}</a>
                  <button className="text-sm text-rose-500 hover:text-rose-700 rounded p-1 hover:bg-rose-500/10" title="Delete this domain (brand, certificate and DNS zone)"
                    onClick={() => deleteWlDomain(d.domain)} disabled={wlBusy}>🗑</button>
                </div>
              </div>
              <div className="px-4 py-3 space-y-2">
                {d.label && (
                  <div className="text-xs text-slate-500 font-mono">
                    label <span className="text-ink-900">{d.label}</span> · script <span className="text-ink-900">https://{d.cdn_host || `cdn.${d.domain}`}/{d.script_name}.min.js</span> · fn <span className="text-ink-900">{d.function_name}()</span> · params <span className="text-ink-900">{d.campaign_param}</span>/<span className="text-ink-900">{d.user_param}</span>
                  </div>
                )}
                <details open={!d.delegated}>
                  <summary className="text-xs text-slate-500 cursor-pointer select-none hover:text-ink-900">
                    Name servers{!d.delegated && <span className="text-amber-600"> — set these at the registrar (or create the records at the current DNS provider)</span>}
                  </summary>
                  <div className="flex items-start gap-2 mt-2">
                    <pre className="flex-1 bg-slate-900 text-slate-100 rounded p-3 text-xs leading-relaxed overflow-x-auto">{d.name_servers.join('\n')}</pre>
                    <Button variant="outline" onClick={() => copyNs(d)}>{copied === d.domain ? '✓' : '⧉ NS'}</Button>
                  </div>
                </details>
                {d.attach_error && <p className="text-xs text-rose-600">Failed to attach certificate: {d.attach_error}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Marcas existentes (embutidas no audience + dinâmicas) e suas variáveis */}
        <div className="mt-6">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Existing brands</div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                  <th className="px-3 py-2.5 font-medium">Label</th>
                  <th className="px-3 py-2.5 font-medium">Script</th>
                  <th className="px-3 py-2.5 font-medium">Function</th>
                  <th className="px-3 py-2.5 font-medium whitespace-nowrap">Params</th>
                  <th className="px-3 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {wlBrands.map((b) => {
                  const scriptUrl = new URL(b.script_url);
                  return (
                  <tr key={b.label} className="hover:bg-surface-muted/60 align-top">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button className="font-semibold text-ink-900 hover:text-brand-600 hover:underline" title={`Check ${b.domain} infrastructure health`}
                        onClick={() => openInfraCheck(b.domain)}>{b.label}</button>
                      <div className="mt-1">{b.builtin
                        ? <span className="inline-flex whitespace-nowrap rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 text-[10px] font-medium">built-in</span>
                        : <span className="inline-flex whitespace-nowrap rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-medium">dynamic</span>}</div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap" title={b.script_url}>
                      <div className="text-ink-900">{scriptUrl.pathname.slice(1)}</div>
                      <div className="text-slate-400 mt-0.5">{scriptUrl.host}</div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{b.function_name}()</td>
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
                      <div className="text-slate-600">{b.campaign_param}</div>
                      <div className="text-slate-400 mt-0.5">{b.user_param}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right">
                      {!b.builtin && <div className="flex items-center justify-end gap-1">
                        <button className="text-xs text-brand-600 rounded px-1.5 py-1 hover:bg-brand-500/10" title="Edit this brand (label included)"
                          onClick={() => editBrand(b)} disabled={wlBusy}>✎ Edit</button>
                        <button className="text-sm text-rose-500 hover:text-rose-700 rounded px-1.5 py-1 hover:bg-rose-500/10" title={`Delete ${b.domain} (brand, certificate and DNS zone)`}
                          onClick={() => deleteWlDomain(b.domain)} disabled={wlBusy}>🗑</button>
                      </div>}
                    </td>
                  </tr>
                  );
                })}
                {wlBrands.length === 0 && <tr><td colSpan={5} className="px-4 py-4 text-slate-400">—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Health check da infra do domínio */}
      <Modal open={infraOpen} onClose={() => setInfraOpen(false)} title={`🩺 ${infraDomain}`}
        footer={<>
          {infraData && !infraData.ok && (
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={fixInfra} disabled={infraBusy}>
              {infraBusy ? 'Fixing…' : '🔧 Fix issues'}
            </Button>
          )}
          <Button variant="outline" onClick={() => openInfraCheck(infraDomain)} disabled={infraBusy}>{infraBusy ? 'Checking…' : '↻ Re-run checks'}</Button>
          <Button variant="outline" onClick={() => setInfraOpen(false)}>Close</Button>
        </>}>
        {infraBusy && !infraData && (
          <div className="py-8 text-center">
            <div className="h-8 w-8 mx-auto rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
            <p className="text-sm text-slate-500 mt-3">Running live checks (DNS, ACM, ALB, HTTPS)…</p>
          </div>
        )}
        {infraData && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${infraData.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {infraData.ok ? '✓ Infrastructure healthy' : '✗ Issues found'}
              </span>
              <span className="text-xs text-slate-400">checked {new Date(infraData.checked_at).toLocaleTimeString()}</span>
            </div>
            {(infraData.actions?.length ?? 0) > 0 && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2">
                <span className="font-semibold">Fixes applied:</span> {infraData.actions!.join(' · ')}
              </div>
            )}
            <div className="rounded-lg border border-border divide-y divide-border">
              {infraData.checks.map((c) => (
                <div key={c.id} className="flex items-start gap-3 px-3 py-2.5">
                  <span className={`mt-0.5 font-bold ${c.ok ? (c.warn ? 'text-amber-500' : 'text-emerald-600') : 'text-rose-600'}`}>
                    {c.ok ? (c.warn ? '⚠' : '✓') : '✗'}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink-900">{c.label}</div>
                    <div className="text-xs text-slate-500 font-mono break-all">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
}
